import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

export type ShopSelectOption = {
    value: string;
    label: string;
};

type Props = {
    id?: string;
    value: string;
    onChange: (value: string) => void;
    options: readonly ShopSelectOption[];
    /** Accessible name when no external label is associated */
    ariaLabel?: string;
    className?: string;
    disabled?: boolean;
    /** Full-width form field (default) vs compact pill */
    fullWidth?: boolean;
};

/**
 * Rounded shop listbox (LanguageSwitcher / ProductSortDropdown pattern).
 * SiteSettings CSS vars only.
 * Menu is portaled to document.body so it is not clipped by overflow-hidden parents
 * (e.g. ProductForm specification cards — Baby Age Range enum).
 */
const ShopSelect: React.FC<Props> = ({
    id,
    value,
    onChange,
    options,
    ariaLabel,
    className = '',
    disabled = false,
    fullWidth = true,
}) => {
    const autoId = useId();
    const controlId = id ?? autoId;
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const rootRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);

    // Value not in options (legacy free-form) → show placeholder, not options[0] by accident
    const current = options.find((o) => o.value === value);
    const placeholder =
        options.find((o) => o.value === '')?.label ?? options[0]?.label ?? '';
    const triggerLabel = current?.label ?? placeholder;

    const updateMenuPosition = () => {
        const btn = buttonRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        const viewportH = window.innerHeight;
        const spaceBelow = viewportH - rect.bottom;
        const preferUp = spaceBelow < 220 && rect.top > spaceBelow;
        const maxH = Math.min(240, preferUp ? rect.top - 16 : spaceBelow - 16);

        setMenuStyle({
            position: 'fixed',
            left: rect.left,
            width: Math.max(rect.width, fullWidth ? rect.width : 192),
            // Above Headless UI settings/confirm dialogs (z-[10040]/z-[10050])
            zIndex: 11000,
            maxHeight: Math.max(120, maxH),
            ...(preferUp
                ? { bottom: viewportH - rect.top + 8, top: 'auto' as const }
                : { top: rect.bottom + 8, bottom: 'auto' as const }),
        });
    };

    useLayoutEffect(() => {
        if (!open) return;
        updateMenuPosition();
    }, [open, options.length, fullWidth]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            const t = e.target as Node;
            if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
            setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        const onReposition = () => updateMenuPosition();
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', onReposition);
        // Capture scroll on any scrollable ancestor / window
        window.addEventListener('scroll', onReposition, true);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [open]);

    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    const menu =
        open && typeof document !== 'undefined'
            ? createPortal(
                  <ul
                      ref={menuRef}
                      role="listbox"
                      data-shop-select-menu=""
                      aria-labelledby={controlId}
                      style={menuStyle}
                      className="overflow-auto border border-[var(--accent-color)]/40 bg-[var(--bg-color)] py-1 shadow-xl rounded-2xl"
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                  >
                      {options.map((opt, index) => {
                          const selected = opt.value === value;
                          // Age ranges like "0-2" are unique as value; index avoids rare duplicate keys
                          return (
                              <li key={`${opt.value}::${index}`} role="option" aria-selected={selected}>
                                  <button
                                      type="button"
                                      onClick={() => {
                                          onChange(opt.value);
                                          setOpen(false);
                                      }}
                                      className={`flex min-h-11 w-full items-center px-4 py-2.5 text-left text-sm transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_55%)] ${
                                          selected
                                              ? 'bg-[color-mix(in_srgb,var(--accent-color),transparent_40%)] font-semibold text-[var(--text-color)]'
                                              : 'text-[var(--text-color)]'
                                      }`}
                                  >
                                      <span className="truncate">{opt.label}</span>
                                  </button>
                              </li>
                          );
                      })}
                  </ul>,
                  document.body
              )
            : null;

    return (
        <div ref={rootRef} className={`relative ${fullWidth ? 'w-full' : 'inline-block'} ${className}`}>
            <button
                ref={buttonRef}
                type="button"
                id={controlId}
                disabled={disabled}
                onClick={() => !disabled && setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                className={`inline-flex min-h-11 items-center justify-between gap-2 border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] px-3 py-2.5 text-left text-base text-[var(--text-color)] outline-none transition focus:border-[var(--highlight-color,var(--accent-color))] focus:ring-2 focus:ring-[var(--highlight-color,var(--accent-color))]/30 disabled:cursor-not-allowed disabled:opacity-60 ${
                    fullWidth ? 'w-full rounded-xl' : 'rounded-full text-sm'
                }`}
            >
                <span className="min-w-0 truncate">{triggerLabel}</span>
                <ChevronDown
                    className={`h-4 w-4 shrink-0 opacity-70 transition ${open ? 'rotate-180' : ''}`}
                    aria-hidden
                />
            </button>
            {menu}
        </div>
    );
};

export default ShopSelect;
